import { Geometry, Position } from 'geojson';

import { PaginatedSearchDto } from 'src/infrastructure/admi-client/response.dto';
import { LoadProfileDto, Optimizer } from './pv-calculator.dto';

export interface PVAnalysisVariantDto {
    id: string;
    name: string;
    maxSystemCapacityAdjustment: number;
    author: string;
    createdAt: Date;
    pvAnalysisId: string;
    pvAnalysisName?: string;
    streetAddress?: string;

    buildings?: PvAnalysisVariationBuilding[];
    mapScreenshot?: PvAnalysisVariationMapScreenshot;
    calculatorParams?: PvAnalysisVariationCalculatorParams;
    calculatorOptimizedResult?: PvAnalysisVariationCalculatorResult;
    calculatorFullRoofResult?: PvAnalysisVariationCalculatorResult;
}

export interface PotentialAnalysisSearchDto extends PaginatedSearchDto {
    searchString?: string;
    include?: string[];
}

export interface PotentialAnalysisListItemDto {
    id: string;
    name: string;

    variants?: {
        id: string;
        name: string;
        author: string;
        createdAt: Date;
        streetAddress: string;
    }[];
}

export interface CreatePVAnalysisVariantDto {
    name: string;
    pvAnalysisId?: string;
    pvAnalysisName?: string;
    streetAddress: string;

    buildings: PvAnalysisVariationBuilding[];
    mapScreenshot: PvAnalysisVariationMapScreenshot;
    mapScreenshotImageData: Blob;
    calculatorParams: PvAnalysisVariationCalculatorParams;
    calculatorOptimizedResult: PvAnalysisVariationCalculatorResult;
    calculatorFullRoofResult: PvAnalysisVariationCalculatorResult;
}

export interface BuildingFunction {
    code: string;
    name: string;
}

export interface PvAnalysisVariationBuilding {
    id?: string;
    name: string;
    buildingFunctionCode: string | null;
    code: string;
    geometry: Geometry;
    roofSegments: PvAnalysisVariationRoofSegment[];
    source: string;
}

export interface PvAnalysisVariationRoofSegment {
    azimuthDegrees: number;
    tiltDegrees: number;
    kwp: number;
    kwhPerKwp: number;

    // the properties below only exist for a PV analysis variation that is about to be created
    // and not loaded from persistance
    center?: Position;
    areaM2?: number;
}

export interface PvAnalysisVariationMapScreenshot {
    center: Position;
    zoom: number;
}

export interface PvAnalysisVariationCalculatorParams {
    optimizer: Optimizer;
    buildingFunctionCode: string;
    fundingRate: number;
    loadProfiles: LoadProfileDto[];
    costModel: string;
    targetKwp: number | null;
    batteryCapacityFactor: number | null;
    limitTo100Kwp: boolean;
    energyPrice: number;
}

export interface PvAnalysisVariationCalculatorResult {
    amortizationYears: number | null;
    balanceAfter30Years: number;
    initialInvestment: number;
    initialInvestmentInclFunding: number;
    profitLossAccumulated: number[];
    rateOfReturn: number;
    annualProduction: number;
    kwp: number;
    batteryCapacity: number;
    selfConsumptionRate: number;
    selfSufficiencyRate?: number;
    loadSummary: [string, number][];
    co2Savings: number;
    trees: number;
    panelCount?: number;
    chartsDay?: {
        load: number[];
        pvProduction: number[];
        selfConsumption: number[];
        batteryCharge: number[];
        batteryDischarge: number[];
    };
    chartsYear?: {
        load: number[];
        pvProduction: number[];
    };
}

export interface PvAnalysisBuildingMetadata {
    function: string | null;
    code: string;
    geometry: Geometry;
    filePath: string;
    municipalityCode: string;
}

export interface GeoLocation {
    address: string;
    lat: number;
    lng: number;
    zoom?: number;
}

export interface Location {
    lat: number;
    lng: number;
}

export interface LocationSearch {
    location: Location;
    radius: number;
}

export enum SalesPrintVariant {
    PORTRAIT = 'PORTRAIT',
    LANDSCAPE = 'LANDSCAPE',
}
